import { ulid } from 'ulid';

// 쿠키 키 상수
const ANON_ID_KEY = 'rw_anon_id';
const SESSION_ID_KEY = 'rw_session_id';
const LAST_ACTIVITY_KEY = 'rw_last_activity';
const VIEW_ID_KEY = 'rw_view_id';
const TRACE_PREFIX = 'rw_trace:';
const INTENT_PREFIX = 'rw_intent:';

// 세션 만료 시간 (30분)
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

// 쿠키 유틸리티 함수
function setCookie(name: string, value: string, days: number = 365): void {
    if (typeof document === 'undefined') return;

    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);

    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax${location.protocol === 'https:' ? ';Secure' : ''
        }`;
}

function getCookie(name: string): string | null {
    if (typeof document === 'undefined') return null;

    const nameEQ = name + '=';
    const ca = document.cookie.split(';');

    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
}

function deleteCookie(name: string): void {
    if (typeof document === 'undefined') return;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
}

// 세션 스토리지 유틸리티
function getSessionItem(key: string): string | null {
    if (typeof sessionStorage === 'undefined') return null;
    try {
        return sessionStorage.getItem(key);
    } catch {
        return null;
    }
}

function setSessionItem(key: string, value: string): void {
    if (typeof sessionStorage === 'undefined') return;
    try {
        sessionStorage.setItem(key, value);
    } catch {
        // 저장 실패 시 무시
    }
}

function removeSessionItem(key: string): void {
    if (typeof sessionStorage === 'undefined') return;
    try {
        sessionStorage.removeItem(key);
    } catch {
        // 삭제 실패 시 무시
    }
}

/**
 * 익명 사용자 ID 관리
 * 1년 쿠키로 저장, 없으면 새로 생성
 */
export function getAnonId(): string {
    let anonId = getCookie(ANON_ID_KEY);

    if (!anonId) {
        anonId = ulid();
        setCookie(ANON_ID_KEY, anonId, 365);
    }

    return anonId;
}

/**
 * 세션 ID 관리
 * 30분 idle 슬라이딩 윈도우
 */
export function getSessionId(): string {
    const now = Date.now();
    const lastActivity = parseInt(getCookie(LAST_ACTIVITY_KEY) || '0', 10);

    let sessionId = getCookie(SESSION_ID_KEY);

    // 세션이 없거나 30분 초과 시 새로 생성
    if (!sessionId || (now - lastActivity) > SESSION_TIMEOUT_MS) {
        sessionId = ulid();
        setCookie(SESSION_ID_KEY, sessionId, 1); // 1일 쿠키
    }

    // 활동 시간 업데이트
    setCookie(LAST_ACTIVITY_KEY, now.toString(), 1);

    return sessionId;
}

/**
 * 세션 무효화 (로그아웃 시 사용)
 */
export function invalidateSession(): void {
    deleteCookie(SESSION_ID_KEY);
    deleteCookie(LAST_ACTIVITY_KEY);
}

/**
 * Trace ID 관리
 * 클릭 시 생성하여 세션 스토리지에 저장
 * 다음 페이지에서 읽어서 사용 후 삭제
 */
export function generateTraceId(): string {
    return ulid();
}

export function setTraceId(path: string, traceId: string): void {
    // 쿼리스트링 제거 - pathname만 사용
    const pathname = path.split('?')[0];
    const key = `${TRACE_PREFIX}${pathname}`;
    setSessionItem(key, traceId);
}

export function getAndClearTraceId(path: string): string | null {
    const key = `${TRACE_PREFIX}${path}`;
    const traceId = getSessionItem(key);

    if (traceId) {
        removeSessionItem(key);
    }

    return traceId;
}

/**
 * View ID 관리
 * 한 화면에 머무른 구간을 나타내는 ID
 * page_view 시 생성, page_leave 시 해제
 */
let currentViewId: string | null = null;

export function generateViewId(): string {
    return ulid();
}

export function setViewId(viewId: string): void {
    currentViewId = viewId;

    // 세션 스토리지에도 저장 (페이지 새로고침 대비)
    setSessionItem(VIEW_ID_KEY, viewId);
}

export function getViewId(): string | null {
    // 메모리 우선, 없으면 세션 스토리지에서 복구
    if (!currentViewId) {
        currentViewId = getSessionItem(VIEW_ID_KEY);
    }
    return currentViewId;
}

export function clearViewId(): void {
    currentViewId = null;
    removeSessionItem(VIEW_ID_KEY);
}

/**
 * Search Intent ID 관리
 * search_open 시 자동 생성 및 저장
 * search_performed 시 자동으로 연결
 */
let currentSearchIntentId: string | null = null;

export function setSearchIntentId(intentId: string): void {
    currentSearchIntentId = intentId;
}

export function getSearchIntentId(): string | null {
    return currentSearchIntentId;
}

export function clearSearchIntentId(): void {
    currentSearchIntentId = null;
}

/**
 * Intent ID 관리
 * 의도 시작 시 생성하여 세션 스토리지에 저장
 * success/fail 시 함께 전달하여 퍼널 추적
 */
export function generateIntentId(): string {
    return ulid();
}

export function setIntentId(kind: string, intentId: string, payload?: any): void {
    const key = `${INTENT_PREFIX}${kind}`;
    console.log("setIntentId", key, intentId, payload);
    const data = {
        intentId,
        payload,
        createdAt: Date.now()
    };
    setSessionItem(key, JSON.stringify(data));
}

export function getIntentId(kind: string): { intentId: string; payload?: any; createdAt: number } | null {
    const key = `${INTENT_PREFIX}${kind}`;
    const data = getSessionItem(key);

    if (!data) return null;

    try {
        return JSON.parse(data);
    } catch {
        return null;
    }
}

export function clearIntentId(kind: string): void {
    const key = `${INTENT_PREFIX}${kind}`;
    removeSessionItem(key);
}

/**
 * Intent 정리 (만료된 항목들 제거)
 * 1시간 이상 된 intent는 제거
 */
export function cleanupExpiredIntents(): void {
    if (typeof sessionStorage === 'undefined') return;

    const now = Date.now();
    const expireTime = 60 * 60 * 1000; // 1시간

    try {
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith(INTENT_PREFIX)) {
                const data = sessionStorage.getItem(key);
                if (data) {
                    try {
                        const parsed = JSON.parse(data);
                        if (now - parsed.createdAt > expireTime) {
                            sessionStorage.removeItem(key);
                        }
                    } catch {
                        // 파싱 실패 시 제거
                        sessionStorage.removeItem(key);
                    }
                }
            }
        }
    } catch {
        // 스토리지 오류 시 무시
    }
}

/**
 * 현재 사용자 ID (로그인 상태)
 */
let currentUserId: string | null = null;

export function setUserId(userId: string | null): void {
    currentUserId = userId;
}

export function getUserId(): string | null {
    return currentUserId;
}

/**
 * A/B 테스트 버킷 정보
 */
let abBuckets: Record<string, string> = {};

export function setABBuckets(buckets: Record<string, string>): void {
    abBuckets = { ...buckets };
}

export function getABBuckets(): Record<string, string> {
    return { ...abBuckets };
}

/**
 * ID 관리자 초기화
 * 앱 시작 시 호출하여 만료된 데이터 정리
 */
export function initIdManager(): void {
    // 세션 ID 갱신 (활동 시간 업데이트)
    getSessionId();

    // 만료된 intent 정리
    cleanupExpiredIntents();
}
